package com.hyperether.goodjob.scenes.components

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshots.SnapshotStateList
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextOverflow
import com.hyperether.goodjob.boxCornerShape
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.repository.remote.model.ParsedAddress
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.ic_map_pin
import com.hyperether.goodjob.resources.password_length
import com.hyperether.goodjob.resources.password_not_visible
import com.hyperether.goodjob.resources.password_visible
import com.hyperether.goodjob.resources.passwords_do_not_match
import com.hyperether.goodjob.resources.start_typing_address
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.EmployeeDetailsText
import com.hyperether.goodjob.theme.InputBorderColor
import com.hyperether.goodjob.theme.InputIconColor
import com.hyperether.goodjob.theme.TextColor
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource

@Composable
fun PasswordInputField(
    text: MutableState<String>,
    placeholder: String,
    modifier: Modifier = Modifier,
    passwordText: String = "",
    isSecondPasswordInput: Boolean = false,
    isRegister: Boolean = false,
    showMismatchError: Boolean = false
) {
    Box(modifier = modifier.fillMaxWidth()) {
        var passVisible by remember { mutableStateOf(false) }
        val isTooShort = text.value.length in 1..7

        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            value = text.value,
            onValueChange = {
                text.value = it.trim()
            },
            placeholder = {
                Text(
                    placeholder, fontSize = 14.toScaleSp(), color = Color.LightGray,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            },
            isError = isTooShort && isRegister,
            visualTransformation = if (passVisible) VisualTransformation.None else PasswordVisualTransformation(),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
            trailingIcon = {
                val image =
                    if (passVisible) Res.drawable.password_visible else Res.drawable.password_not_visible
                IconButton(onClick = { passVisible = !passVisible }) {
                    Icon(
                        painter = painterResource(image),
                        contentDescription = "Show/Hide password",
                        tint = InputIconColor
                    )
                }
            },
            supportingText = {
                if (isTooShort && isRegister) {
                    Text(
                        text = stringResource(Res.string.password_length),
                        color = Color.Red,
                        fontSize = 12.toScaleSp()
                    )
                } else if (showMismatchError) {
                    Text(
                        text = stringResource(Res.string.passwords_do_not_match),
                        color = Color.Red,
                        fontSize = 12.toScaleSp(),
                        modifier = Modifier.padding(start = 8.toScaleDp(), top = 4.toScaleDp())
                    )
                }
            },
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor,
                focusedBorderColor = if ((isSecondPasswordInput && text.value != passwordText) || (isTooShort && isRegister)) Color.Red else BlueApp,
                cursorColor = BlueApp
            )
        )
    }
}

@Composable
fun PhoneInputField(
    text: MutableState<String>,
    label: String,
    placeholder: String,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = if (getPlatformChecker().isMobile())
            modifier
                .fillMaxWidth() else modifier
            .fillMaxWidth()
            .height(56.toScaleDp())
    ) {
        OutlinedTextField(
            modifier = Modifier.fillMaxSize(),
            textStyle = TextStyle(fontSize = 14.toScaleSp()),
            value = text.value,
            onValueChange = { newValue ->
                if (newValue.length <= 15 && (newValue.isEmpty() || newValue.all { it.isDigit() } ||
                            (newValue.startsWith("+") && newValue.drop(1).all { it.isDigit() }))) {
                    text.value = newValue
                }
            },
            singleLine = true,
            label = {
                Text(
                    label, fontSize = 14.toScaleSp(), color = TextColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            placeholder = {
                Text(
                    placeholder,
                    fontSize = 14.toScaleSp(),
                    color = Color.LightGray,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            keyboardOptions = KeyboardOptions.Default.copy(keyboardType = KeyboardType.Phone),
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor,
                focusedBorderColor = BlueApp,
                cursorColor = BlueApp
            )
        )
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun AddressInputField(
    textCity: MutableState<String>? = null,
    textCountry: MutableState<String>? = null,
    text: MutableState<String>,
    label: String,
    placeholder: String,
    modifier: Modifier,
    places: SnapshotStateList<ParsedAddress>,
    onSearch: (String) -> Unit,
    onSelect: (ParsedAddress) -> Unit
) {
    var isTyping by remember { mutableStateOf(false) }

    val bringIntoViewRequester = remember { BringIntoViewRequester() }
    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(places.size) {
        if (places.isNotEmpty()) {
            coroutineScope.launch {
                bringIntoViewRequester.bringIntoView()
            }
        }
    }

    LaunchedEffect(text.value) {
        if (isTyping) {
            var textToSearch = text.value
            if (textCity != null) {
                textToSearch = textToSearch.plus(", ${textCity.value}")
            }
            if (textCountry != null) {
                textToSearch = textToSearch.plus(", ${textCountry.value}")
            }
            isTyping = false
            delay(500) // Wait for user to stop typing
            onSearch(textToSearch) // Call API after delay
        }
    }

    Column(
        modifier = if (getPlatformChecker().isMobile()) modifier
            .fillMaxWidth() else modifier
            .fillMaxWidth()
            .height(56.toScaleDp())
    ) {
        OutlinedTextField(
            modifier = Modifier.fillMaxSize(),
            value = text.value,
            onValueChange = {
                text.value = ""
                text.value = it
                isTyping = true
            },
            textStyle = TextStyle(fontSize = 14.toScaleSp()),
            label = {
                Text(label, fontSize = 14.toScaleSp(), color = TextColor)
            },
            placeholder = {
                Text(
                    placeholder,
                    fontSize = 14.toScaleSp(),
                    color = Color.LightGray
                )
            },
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor,
                focusedBorderColor = BlueApp,
                cursorColor = BlueApp
            )
        )

        if (places.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color.White)
                    .bringIntoViewRequester(bringIntoViewRequester)
                    .border(1.toScaleDp(), Color.Gray, boxCornerShape()).padding(10.toScaleDp())
            ) {
                Column(
                    modifier = Modifier.padding(16.toScaleDp())
                ) {
                    places.forEach {
                        Text(
                            it.formattedAddress, modifier = Modifier
                                .padding(all = 8.toScaleDp())
                                .clickable {
                                    onSelect(it)
                                    places.clear()
                                },
                            fontSize = 14.toScaleSp()
                        )
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun AddressInputFieldWeb(
    textCity: MutableState<String>? = null,
    textCountry: MutableState<String>? = null,
    text: MutableState<String>,
    label: String,
    modifier: Modifier,
    places: SnapshotStateList<ParsedAddress>,
    onSearch: (String) -> Unit,
    onSelect: (ParsedAddress) -> Unit,
    textColor: Color = EmployeeDetailsText,
    placeholderColor: Color = Color.LightGray,
    focusManager: FocusManager,
    enabled: Boolean = true
) {
    var isTyping by remember { mutableStateOf(false) }

    val bringIntoViewRequester = remember { BringIntoViewRequester() }
    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(places.size) {
        if (places.isNotEmpty()) {
            coroutineScope.launch {
                bringIntoViewRequester.bringIntoView()
            }
        }
    }

    LaunchedEffect(text.value) {
        if (isTyping) {
            var textToSearch = text.value
            if (textCity != null) {
                textToSearch = textToSearch.plus(", ${textCity.value}")
            }
            if (textCountry != null) {
                textToSearch = textToSearch.plus(", ${textCountry.value}")
            }
            isTyping = false
            delay(500) // Wait for user to stop typing
            onSearch(textToSearch) // Call API after delay
        }
    }

    Row(
        modifier = modifier
            .onPreviewKeyEvent {
                when {
                    KeyEventType.KeyUp == it.type && Key.Tab == it.key -> {
                        focusManager.moveFocus(FocusDirection.Next)
                        true
                    }

                    else -> false
                }
            }
            .then(modifier)
            .fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.weight(1f)
        ) {
            Image(
                modifier = Modifier.size(20.toScaleDp()),
                painter = painterResource(Res.drawable.ic_map_pin),
                contentDescription = null
            )
            Text(
                text = label,
                color = textColor,
                fontSize = 14.toScaleSp(),
                modifier = Modifier.padding(start = 20.toScaleDp()),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
        Column(
            modifier = modifier
                .weight(2f)
        ) {
            OutlinedTextField(
                value = text.value,
                onValueChange = {
                    text.value = ""
                    text.value = it
                    isTyping = true
                },
                textStyle = TextStyle(fontSize = 14.toScaleSp()),
                placeholder = {
                    androidx.compose.material.Text(
                        text = stringResource(Res.string.start_typing_address),
                        color = placeholderColor,
                        fontSize = 14.toScaleSp()
                    )
                },
                enabled = enabled,
                singleLine = true,
                modifier = Modifier.fillMaxWidth().height(56.toScaleDp()),
                colors = OutlinedTextFieldDefaults.colors(
                    disabledBorderColor = InputBorderColor,
                    unfocusedBorderColor = InputBorderColor,
                    disabledTextColor = TextColor,
                    focusedBorderColor = BlueApp,
                    cursorColor = BlueApp
                ),
            )
            if (places.isNotEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(Color.White)
                        .bringIntoViewRequester(bringIntoViewRequester)
                        .border(1.toScaleDp(), Color.Gray, boxCornerShape())
                ) {
                    Column(
                        modifier = Modifier.padding(16.toScaleDp())
                    ) {
                        places.forEach {
                            androidx.compose.material.Text(
                                it.formattedAddress,
                                modifier = Modifier
                                    .padding(all = 8.toScaleDp())
                                    .clickableWithPointer {
                                        onSelect(it)
                                        places.clear()
                                    },
                                fontSize = 14.toScaleSp()
                            )
                        }
                    }
                }
            }
        }
    }
}